/**
 * FirstVisitBanner.jsx
 * 首次访问引导条 — 仅对新访客展示，localStorage 控制
 * 底部固定玻璃条，3个步骤入口 + 关闭按钮
 */
import React, { useState, useEffect } from "react";

const STORAGE_KEY = "lumina_visited_v1";

const STEPS = [
  {
    emoji: "✶",
    label_zh: "填写生辰",
    label_en: "Enter Birth Info",
    desc_zh: "八字命盘 · 免费",
    desc_en: "Free Ba Zi Chart",
    color: "#10b981",
    page: "audit",
  },
  {
    emoji: "☯",
    label_zh: "六爻定事",
    label_en: "Cast a Reading",
    desc_zh: "占卜决策 · $29",
    desc_en: "Divination · $29",
    color: "#f59e0b",
    page: "liuyao",
  },
  {
    emoji: "◈",
    label_zh: "能量计划",
    label_en: "Energy Plans",
    desc_zh: "深度报告 · L1+",
    desc_en: "Full Report · L1+",
    color: "#8b5cf6",
    page: "pricing",
  },
];

export default function FirstVisitBanner({ setPage, lang = "en" }) {
  const zh = lang === "zh";
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = () => {
    setLeaving(true);
    localStorage.setItem(STORAGE_KEY, "1");
    setTimeout(() => setVisible(false), 400);
  };

  const handleStep = (page) => {
    dismiss();
    setPage(page);
  };

  if (!visible) return null;

  return (
    <>
      <style>{`
        @keyframes slideUpBanner {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes slideDownBanner {
          from { transform: translateY(0);    opacity: 1; }
          to   { transform: translateY(100%); opacity: 0; }
        }
        .fvb-root {
          animation: ${leaving ? "slideDownBanner" : "slideUpBanner"} 0.4s cubic-bezier(.32,.7,.43,1) forwards;
        }
        .fvb-step {
          transition: background 0.2s, transform 0.2s;
          cursor: pointer;
        }
        .fvb-step:hover {
          transform: translateY(-3px);
          background: rgba(255,255,255,0.10) !important;
        }
        .fvb-close:hover { opacity: 1 !important; }
      `}</style>

      <div
        style={{
          position: "fixed", inset: 0, zIndex: 9998,
          background: "rgba(0,0,0,0.15)",
          backdropFilter: "blur(1px)",
          pointerEvents: "none",
        }}
      />

      <div
        className="fvb-root"
        style={{
          position: "fixed",
          bottom: 0, left: 0, right: 0,
          zIndex: 9999,
          background: "linear-gradient(135deg,rgba(10,15,30,0.97),rgba(13,20,40,0.97))",
          backdropFilter: "blur(24px) saturate(1.4)",
          borderTop: "1px solid rgba(255,255,255,0.10)",
          padding: "20px 28px 24px",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.35)",
        }}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}>
          <div>
            <div style={{
              fontSize: 13, fontWeight: 700, letterSpacing: ".12em",
              color: "#fbbf24", textTransform: "uppercase", marginBottom: 2,
            }}>
              {zh ? "欢迎来到 LUMINA" : "Welcome to LUMINA"}
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>
              {zh
                ? "三步开始你的能量之旅 · 发现你的能量蔗图"
                : "3 steps to begin · Discover your energy blueprint"}
            </div>
          </div>
          <button
            className="fvb-close"
            onClick={dismiss}
            style={{
              background: "none", border: "none", color: "#6b7280",
              fontSize: 20, cursor: "pointer", opacity: 0.7, lineHeight: 1,
              padding: "4px 8px", borderRadius: 6,
              transition: "opacity 0.2s",
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
        }}>
          {STEPS.map((s, i) => (
            <div
              key={s.page}
              className="fvb-step"
              onClick={() => handleStep(s.page)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "rgba(255,255,255,0.05)",
                border: `1px solid ${s.color}33`,
                borderRadius: 10,
                padding: "10px 16px",
                flex: "1 1 140px",
                minWidth: 140,
              }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: `${s.color}20`,
                border: `1.5px solid ${s.color}60`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, color: s.color, fontWeight: 700, flexShrink: 0,
              }}>
                {i + 1}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.3 }}>
                  {zh ? s.label_zh : s.label_en}
                </div>
                <div style={{ fontSize: 11, color: s.color, marginTop: 1 }}>
                  {zh ? s.desc_zh : s.desc_en}
                </div>
              </div>
              <div style={{ marginLeft: "auto", color: "#6b7280", fontSize: 14 }}>→</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12, textAlign: "center" }}>
          <button
            onClick={dismiss}
            style={{
              background: "none", border: "none", color: "#475569",
              fontSize: 11, cursor: "pointer", letterSpacing: ".06em",
              textDecoration: "underline", textDecorationColor: "rgba(71,85,105,0.4)",
            }}
          >
            {zh ? "先浏览 · 稍后再说" : "Browse first · Skip for now"}
          </button>
        </div>
      </div>
    </>
  );
}
